// @ vendors
const Immutable = require('immutable');
// @ constants
const actionTypes = require('constants/RAConstants/actionTypes');
const { CONTRIBUTION_PERIOD_MONTHLY, MINIMUM_OBJECTIVE_AMOUNT } = require('constants/RAConstants/index');
// @ utilities
import { getDateWithOffset, formatDateToYYYYMMDD, getDayStart, getDay } from 'utilities/dateHelper/dateHelper';
const todayDate = formatDateToYYYYMMDD(new Date());
const minDate = getDateWithOffset(getDayStart(), { days: 1, years: 1 });

const initialState = Immutable.fromJS({
    clientRiskProfile: '',
    clientRiskProfileDescription: '',
    maxInvestmentProfile: 0,
    selectedStrategy: 0,
    objective: '',
    objectiveAmount: 0,
    objectiveAmountCurrency: 'EUR',
    objectiveEndDate: minDate,
    objectiveType: '',
    initialContributionAmount: '',
    recurringContributionAmount: 0,
    periodicity: CONTRIBUTION_PERIOD_MONTHLY,
    recurringContributionDay: getDay(todayDate),
    isFetchingProfile: false,
    isSavingSimulation: false,
    isFetchedSimulation: false,
    saveSuccess: false,
    error: null,
    errorAmountMin: false,
    errorInitialMin: false,
    errorPeriodicityMin: false,
});

const parseGoalKey = {
    investmentAmount: 'objectiveAmount',
    investmentTitle: 'objective',
    investmentDate: 'objectiveEndDate',
};

const RACustomPlanByObjective = (state = initialState, action) => {
    switch (action.type) {
        case actionTypes.RA_MANAGEMENT_PLAN_RESET:
            return initialState;
        case actionTypes.RA_CUSTOM_PLAN_BY_OBJECTIVE_UPDATE_DESCRIPTION:
            return state.merge({
                objective: action.payload.title,
            });
        case actionTypes.RA_CUSTOM_PLAN_BY_OBJECTIVE_SET_OBJECTIVE_AMOUNT:
            const errorAmount = action.payload.amount !== 0 && action.payload.amount < MINIMUM_OBJECTIVE_AMOUNT;

            return state.merge({
                objectiveAmount: action.payload.amount,
                errorAmountMin: errorAmount,
            });
        case actionTypes.RA_SET_GOAL_SET_INVESTMENT_GOAL_VALUE:
            const { key, value } = action.payload;
            const goalKey = parseGoalKey[key];
            const parsedValue = goalKey === 'objectiveEndDate' ? getDayStart(value) : value;

            return state.merge({
                [goalKey]: parsedValue,
            });
        case actionTypes.RA_UPDATE_SELECTED_GOAL:
            const { goalSelected } = action.payload;
            const currentObjectiveType = state.get('objectiveType');
            const newObjectiveType = currentObjectiveType !== goalSelected.id ? goalSelected.id : '';
            const newObjectiveName = (!!newObjectiveType && goalSelected.label) || '';

            return state.merge({
                objectiveType: newObjectiveType,
                objective: newObjectiveName,
                objectiveAmount: 0,
                objectiveEndDate: minDate,
            });
        case actionTypes.RA_CUSTOM_PLAN_BY_OBJECTIVE_SET_OBJECTIVE_END_DATE:
            return state.merge({
                objectiveEndDate: action.payload.date,
            });
        case actionTypes.RA_CUSTOM_PLAN_BY_OBJECTIVE_SET_INITIAL_CONTRIBUTION_AMOUNT:
            const errorFlagContribution =
                action.payload.investment !== 0 && action.payload.investment < MINIMUM_OBJECTIVE_AMOUNT;

            return state.merge({
                initialContributionAmount: action.payload.investment,
                errorInitialMin: errorFlagContribution,
            });
        case actionTypes.RA_CUSTOM_PLAN_BY_OBJECTIVE_SET_RECURRING_CONTRIBUTION_AMOUNT:
            const errorFlagRecurring = action.payload.amount !== 0 && action.payload.amount < 1;

            return state.merge({
                recurringContributionAmount: action.payload.amount,
                errorPeriodicityMin: errorFlagRecurring,
            });
        case actionTypes.RA_CUSTOM_PLAN_BY_OBJECTIVE_SET_PERIODICITY:
            return state.merge({
                periodicity: action.payload.periodicity,
            });
        case actionTypes.RA_CUSTOM_PLAN_BY_OBJECTIVE_SET_CONTRIBUTION_DAY:
            return state.merge({
                recurringContributionDay: action.payload.day,
            });
        case actionTypes.RA_CUSTOM_PLAN_BY_OBJECTIVE_SET_SELECTED_STRATEGY:
            return state.set('selectedStrategy', action.payload);

        case actionTypes.RA_CUSTOM_PLAN_BY_OBJECTIVE_DATA_FROM_SIMULATION:
            const immSimulation = action.payload;
            let newState = state.merge({
                isFetchedSimulation: true,
                initialContributionAmount: immSimulation.get('initialContribution'),
                selectedStrategy: immSimulation.get('selectedPortfolio'),
                objective: immSimulation.get('name'),
                objectiveType: immSimulation.get('type'),
                objectiveAmount: immSimulation.get('amount'),
                objectiveEndDate: immSimulation.get('endDate') ? new Date(immSimulation.get('endDate')) : null,
                errorAmountMin:
                    immSimulation.get('amount') < MINIMUM_OBJECTIVE_AMOUNT && immSimulation.get('amount') > 0,
                errorInitialMin:
                    immSimulation.get('initialContribution') < MINIMUM_OBJECTIVE_AMOUNT &&
                    immSimulation.get('initialContribution') > 0,
            });

            if (immSimulation.get('hasRecurringTransfer')) {
                newState = newState.merge({
                    periodicity: immSimulation.getIn(['recurringTransfer', 'frequencyCode']),
                    recurringContributionAmount: immSimulation.getIn(['recurringTransfer', 'amount']),
                    recurringContributionDay: immSimulation.getIn(['recurringTransfer', 'cutDay']),
                    errorPeriodicityMin:
                        immSimulation.getIn(['recurringTransfer', 'amount']) < 1 &&
                        immSimulation.getIn(['recurringTransfer', 'amount']) > 0,
                });
            }

            return newState;
        case actionTypes.RA_MANAGEMENT_PLAN_GET_INVESTMENT_PROFILE_REQUEST:
            return state.merge({
                isFetchingProfile: true,
            });
        case actionTypes.RA_MANAGEMENT_PLAN_GET_INVESTMENT_PROFILE_FAILURE:
            return state.merge({
                error: action.payload.error,
            });
        case actionTypes.RA_MANAGEMENT_PLAN_GET_INVESTMENT_PROFILE_SUCCESS:
            const { initialContribution, selectedPortfolio, hasRecurringTransfer, name, type, amount, endDate, id } =
                action.payload.data;
            const objectiveType = type ? type.toLowerCase() : '';

            let newCustomState = state.merge({
                initialContributionAmount: initialContribution || '',
                isFetchingProfile: false,
                selectedStrategy: selectedPortfolio || 0,
                objective: name || '',
                objectiveType,
                objectiveAmount: amount || 0,
                objectiveEndDate: endDate ? new Date(endDate) : '',
                simulationId: id,
            });

            if (hasRecurringTransfer) {
                const { amount, cutDay, frequencyCode } = action.payload.data.recurringTransfer;

                const recurringTransfer = {
                    periodicity: frequencyCode,
                    recurringContributionAmount: amount,
                    recurringContributionDay: cutDay,
                };
                newCustomState = newCustomState.merge(recurringTransfer);
            }

            return newCustomState;
        case actionTypes.RA_CUSTOM_PLAN_BY_OBJECTIVE_UPDATE_GOAL_ONBOARDING:
            return state.merge({
                objective: action.payload.objective,
                objectiveType: action.payload.objectiveType,
                objectiveAmount: action.payload.objectiveAmount,
                objectiveEndDate: new Date(action.payload.objectiveEndDate),
            });
        default:
            return state;
    }
};

module.exports = RACustomPlanByObjective;
